import { Progress, Tooltip } from 'antd';
import React from 'react';
import { Tag } from '../../../components/tag';
import { Doc31CouponPromotion } from '../../../types/doc31';
import { DOC31_COUPON_STATUS_LABELS } from '../../../utils/doc31';
import { couponItem, formatPeriod } from './form';
import style from './style.module.scss';

export const CouponIdentity = ({
    coupon,
}: {
    coupon: Doc31CouponPromotion;
}) => {
    const en = couponItem(coupon, 'en');
    const fr = couponItem(coupon, 'fr');
    return (
        <div className={style.identity}>
            {en?.thumbnail ? (
                <img className={style.thumbnail} src={en.thumbnail} alt="" />
            ) : (
                <span className={style.thumbnail} aria-hidden />
            )}
            <div className={style.identity_text}>
                <span className={style.title}>{en?.title ?? '—'}</span>
                {fr?.title && <span className={style.muted}>{fr.title}</span>}
                <span className={style.caption}>
                    Promotion {coupon.promotionId}
                </span>
            </div>
        </div>
    );
};

export const ProvincesCell = ({
    ids,
    names,
}: {
    ids: number[];
    /** Province names by id, once the business units are loaded. */
    names: Map<number, string> | null;
}) => {
    const count = `${ids.length} ${
        ids.length === 1 ? 'province' : 'provinces'
    }`;
    if (!names || ids.length === 0) {
        return <span className={style.numeric}>{count}</span>;
    }
    return (
        <Tooltip title={ids.map((id) => names.get(id) ?? `#${id}`).join(', ')}>
            <span className={`${style.numeric} ${style.hint}`}>{count}</span>
        </Tooltip>
    );
};

/** Redeemed inside issued inside quantity, like the RPP inventory bar. */
export const StockCell = ({ coupon }: { coupon: Doc31CouponPromotion }) => {
    const { quantity, issuedQuantity, redeemedQuantity } = coupon;
    if (!quantity) {
        return (
            <span className={style.numeric}>
                {issuedQuantity.toLocaleString()} issued
            </span>
        );
    }
    const issued = Math.min(Math.max(issuedQuantity, 0), quantity);
    const redeemed = Math.min(Math.max(redeemedQuantity, 0), issued);
    const available =
        coupon.availableQuantity ?? Math.max(quantity - issuedQuantity, 0);
    return (
        <div className={style.stock}>
            <Progress
                aria-label={`${issued.toLocaleString()} of ${quantity.toLocaleString()} issued; ${redeemed.toLocaleString()} redeemed`}
                percent={(issued / quantity) * 100}
                success={{
                    percent: (redeemed / quantity) * 100,
                    strokeColor: 'var(--Success-6)',
                }}
                showInfo={false}
                strokeColor="var(--Neutral-8)"
                trailColor="var(--Neutral-5)"
                strokeWidth={6}
            />
            <div className={style.stock_numbers}>
                <span className={style.numeric}>
                    {`${issuedQuantity.toLocaleString()} / ${quantity.toLocaleString()}`}
                </span>
                <span className={`${style.numeric} ${style.muted}`}>
                    {available.toLocaleString()} available
                </span>
            </div>
        </div>
    );
};

export const PacingBars = ({ coupon }: { coupon: Doc31CouponPromotion }) => {
    const periods = coupon.selection.pacingTargets;
    if (periods.length === 0) {
        return <span className={style.muted}>—</span>;
    }
    const highest = Math.max(
        ...periods.map((period) => period.targetAwards),
        1
    );
    return (
        <div className={style.pacing_bars}>
            {periods.map((period) => {
                const label = `${formatPeriod(
                    period.periodStart,
                    period.periodEnd
                )} · ${period.targetAwards.toLocaleString()}`;
                return (
                    <Tooltip key={period.periodStart} title={label}>
                        <span
                            className={style.pacing_bar}
                            aria-label={label}
                            style={{
                                height: `${Math.max(
                                    (period.targetAwards / highest) * 100,
                                    8
                                )}%`,
                            }}
                        />
                    </Tooltip>
                );
            })}
        </div>
    );
};

const STATUS_VARIANTS = {
    draft: 'grey',
    active: 'success',
    withdrawn: 'error',
} as const;

export const StatusTags = ({ coupon }: { coupon: Doc31CouponPromotion }) => (
    <div className={style.tags}>
        <Tag variant={STATUS_VARIANTS[coupon.selection.status]}>
            {DOC31_COUPON_STATUS_LABELS[coupon.selection.status]}
        </Tag>
        {!coupon.isActive && <Tag variant="grey">Inactive</Tag>}
    </div>
);
